import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Divider,
    InputAdornment,
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext'; // To get superadmin ID

// Types
type OrganizationOption = {
    id: string;
    name: string;
    wallet_balance: number;
    management_fee_due: number;
};

export function RecordPaymentPage() {
    const { profile } = useAuth(); // Get user profile

    // Form State
    const [selectedOrgId, setSelectedOrgId] = useState<string>('');
    const [amountReceived, setAmountReceived] = useState<string>(''); // Store as string for easier input handling
    const [paymentReference, setPaymentReference] = useState<string>('');

    // Data State
    const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
    const [selectedOrgDetails, setSelectedOrgDetails] = useState<OrganizationOption | null>(null);

    // Control State
    const [loadingData, setLoadingData] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Fetch organizations
    const fetchOrganizations = useCallback(async () => {
        setLoadingData(true);
        setError(null);
        try {
            const { data, error: fetchError } = await supabase
                .from('organizations')
                .select('id, name, wallet_balance, management_fee_due') // Fetch necessary details
                .order('name');

            if (fetchError) throw fetchError;
            setOrganizations(data || []);
        } catch (err: any) {
            console.error("Error fetching organizations:", err);
            setError(err.message || 'Failed to load organizations.');
            setOrganizations([]);
        } finally {
            setLoadingData(false);
        }
    }, []);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    // Update selected org details display when org changes
    useEffect(() => {
        if (selectedOrgId) {
            const org = organizations.find(o => o.id === selectedOrgId);
            setSelectedOrgDetails(org || null);
        } else {
            setSelectedOrgDetails(null);
        }
    }, [selectedOrgId, organizations]);

    // --- Handlers ---
    const handleOrgChange = (event: SelectChangeEvent<string>) => {
        setSelectedOrgId(event.target.value as string);
        setError(null); // Clear errors/success on org change
        setSuccessMessage(null);
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // Allow only numbers and a single decimal point
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setAmountReceived(value);
        }
    };

    const handleReferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPaymentReference(event.target.value);
    };

    // --- Format Currency ---
    const formatCurrency = (amount: number | null | undefined) => {
        if (amount === null || amount === undefined) return 'R-.--';
        // Consider locale formatting for production
        return `R${amount.toFixed(2)}`;
    };

    // --- Handle Submit (Actual RPC Call) ---
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage(null);
        setSubmitting(true);

        // Basic Validation
        const numericAmount = parseFloat(amountReceived);
        if (!selectedOrgId || isNaN(numericAmount) || numericAmount <= 0) {
            setError('Please select an organization and enter a valid positive amount.');
            setSubmitting(false);
            return;
        }
        if (!profile?.id) {
            setError('Superadmin user ID not found. Cannot process payment.');
             setSubmitting(false);
            return;
        }

        try {
             console.log("Calling RPC record_organization_payment with:", {
                p_organization_id: selectedOrgId,
                p_amount_received: numericAmount,
                p_payment_reference: paymentReference || null, // Pass null if empty
                p_recorded_by: profile.id,
            });

            // Call the RPC function
            const { data: rpcResult, error: rpcError } = await supabase.rpc(
                'record_organization_payment',
                {
                    p_organization_id: selectedOrgId,
                    p_amount_received: numericAmount,
                    p_payment_reference: paymentReference || null,
                    p_recorded_by: profile.id,
                }
            );

            if (rpcError) {
                console.error("RPC Error:", rpcError);
                throw new Error(`RPC Error: ${rpcError.message}`);
            }

            console.log("RPC Result:", rpcResult);

            // Check the success flag returned by the RPC function
            if (rpcResult && rpcResult.success) {
                setSuccessMessage(
                    `${rpcResult.message} Fees Settled: ${formatCurrency(rpcResult.allocated_to_fees)}, ` +
                    `Added to Wallet: ${formatCurrency(rpcResult.allocated_to_wallet)}. ` +
                    `New Wallet Balance: ${formatCurrency(rpcResult.new_wallet_balance)}, ` +
                    `New Fees Due: ${formatCurrency(rpcResult.new_management_fee_due)}`
                );
                // Refresh org data to show updated balances
                fetchOrganizations(); 
                // Reset form
                setSelectedOrgId('');
                setAmountReceived('');
                setPaymentReference('');
                setSelectedOrgDetails(null);
            } else {
                 // Use the error message from the RPC function if available
                 throw new Error(rpcResult?.error || 'Payment recording failed. Check RPC logs.');
            }

        } catch (err: any) {
             console.error("Error recording payment:", err);
             setError(err.message || 'Failed to record payment.');
        } finally {
            setSubmitting(false);
        }
    };

    // --- Render Logic ---
    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Record Organization Payment
            </Typography>

            {loadingData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="body1" gutterBottom>
                        Use this form to record a payment received from an organization. The payment will first be allocated to any outstanding management fees, and the remainder will be added to their top-up wallet balance.
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                        )}
                        {successMessage && (
                            <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
                        )}

                        <Grid container spacing={3}>
                            {/* Column 1: Form Fields */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth margin="normal" required error={!selectedOrgId && !!error}>
                                    <InputLabel id="org-select-label">Organization</InputLabel>
                                    <Select
                                        labelId="org-select-label"
                                        value={selectedOrgId}
                                        label="Organization"
                                        onChange={handleOrgChange}
                                        disabled={submitting}
                                    >
                                        <MenuItem value="">
                                            <em>Select Organization...</em>
                                        </MenuItem>
                                        {organizations.map((org) => (
                                            <MenuItem key={org.id} value={org.id}>
                                                {org.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    id="amountReceived"
                                    label="Amount Received"
                                    name="amountReceived"
                                    value={amountReceived}
                                    onChange={handleAmountChange}
                                    disabled={submitting}
                                    error={!amountReceived && !!error}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">R</InputAdornment>,
                                    }}
                                    inputProps={{
                                        inputMode: 'decimal',
                                        pattern: '^\\d*\\.?\\d*$',
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    id="paymentReference"
                                    label="Payment Reference (Optional)"
                                    name="paymentReference"
                                    value={paymentReference}
                                    onChange={handleReferenceChange}
                                    disabled={submitting}
                                />
                            </Grid>

                            {/* Column 2: Org Details Display */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: '150px' }}>
                                    <Typography variant="h6" gutterBottom>Selected Organization Details</Typography>
                                    {selectedOrgDetails ? (
                                        <>
                                            <Typography variant="body2">
                                                Name: <strong>{selectedOrgDetails.name}</strong>
                                            </Typography>
                                            <Typography variant="body2">
                                                Current Fees Due: <strong>{formatCurrency(selectedOrgDetails.management_fee_due)}</strong>
                                            </Typography>
                                            <Typography variant="body2">
                                                Current Wallet Balance: <strong>{formatCurrency(selectedOrgDetails.wallet_balance)}</strong>
                                            </Typography>
                                            <Divider sx={{ my: 1 }} />
                                             <Typography variant="caption" color="text.secondary">
                                                Balances shown before recording this payment.
                                             </Typography>

                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Select an organization to view details.
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={submitting || !selectedOrgId || !amountReceived || parseFloat(amountReceived) <= 0}
                                sx={{ position: 'relative' }}
                            >
                                Record Payment
                                {submitting && (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            marginTop: '-12px',
                                            marginLeft: '-12px',
                                        }}
                                    />
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}
        </Container>
    );
} 